import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { auth } from "../../../config/firebaseConfig";
import {
  saveSnippet,
  saveSprite,
  loadSnippetData,
  loadSpriteData,
} from "../services/backpackService";

/**
 * Hook to manage Backpack drawer, saving snippets and sprites to cloud,
 * and injecting saved assets into Scratch iframes or code editors.
 */
export const useWorkspaceBackpackBridge = ({
  selectedChild,
  activeWorkspaceEnv,
  parentsDetails,
}) => {
  const [showBackpackDrawer, setShowBackpackDrawer] = useState(false);
  const [showSaveSnippetDialog, setShowSaveSnippetDialog] = useState(false);
  const [activeSnippetToSave, setActiveSnippetToSave] = useState(null);
  const [showSaveSpriteDialog, setShowSaveSpriteDialog] = useState(false);
  const [activeSpriteToSave, setActiveSpriteToSave] = useState(null);
  const [activeContextEnv, setActiveContextEnv] = useState(activeWorkspaceEnv);

  // Synchronize activeContextEnv with activeWorkspaceEnv
  useEffect(() => {
    setActiveContextEnv(activeWorkspaceEnv);
  }, [activeWorkspaceEnv]);

  // Listen for context switch between editor tabs (e.g. Editor vs Convert Code tabs)
  useEffect(() => {
    const handleContextChange = (e) => {
      if (e.detail?.environment) {
        setActiveContextEnv(e.detail.environment);
      }
    };
    window.addEventListener(
      "workspace-active-context-changed",
      handleContextChange
    );
    return () => {
      window.removeEventListener(
        "workspace-active-context-changed",
        handleContextChange
      );
    };
  }, []);

  const handleTriggerSaveSnippet = useCallback((snippetPayload) => {
    setActiveSnippetToSave(snippetPayload);
    setShowSaveSnippetDialog(true);
  }, []);

  // Listen for snippet save requests dispatched globally from editor panels
  useEffect(() => {
    const handleGlobalSaveSnippet = (e) => {
      if (e.detail) {
        handleTriggerSaveSnippet(e.detail);
      }
    };
    window.addEventListener(
      "trigger-save-backpack-snippet",
      handleGlobalSaveSnippet
    );
    return () => {
      window.removeEventListener(
        "trigger-save-backpack-snippet",
        handleGlobalSaveSnippet
      );
    };
  }, [handleTriggerSaveSnippet]);

  const handleSaveSnippetToCloud = useCallback(
    async ({ name, description }) => {
      if (!selectedChild) {
        toast.error("Please select a student before saving a snippet.");
        throw new Error("Please select a student before saving a snippet.");
      }
      if (!activeSnippetToSave) {
        toast.error("No snippet data found to save.");
        throw new Error("No snippet data found to save.");
      }

      const parentId =
        parentsDetails?.id ||
        parentsDetails?.uid ||
        selectedChild?.parentUid ||
        auth?.currentUser?.uid;
      const childId = selectedChild.id || selectedChild.uid;

      const snippetLang =
        activeSnippetToSave.language ||
        (activeWorkspaceEnv === "python"
          ? "python"
          : activeWorkspaceEnv === "javascript"
          ? "javascript"
          : null);
      const snippetEnv =
        activeSnippetToSave.environment ||
        (snippetLang === "python"
          ? "python"
          : snippetLang === "javascript"
          ? "javascript"
          : activeWorkspaceEnv);

      try {
        await saveSnippet({
          childId,
          parentUid: parentId,
          name,
          description,
          environment: snippetEnv,
          snippetJson: activeSnippetToSave.snippetJson || null,
          code: activeSnippetToSave.code || null,
          language: snippetLang,
        });

        toast.success("Snippet saved successfully");
      } catch (err) {
        console.error("Failed to save snippet:", err);
        toast.error(err.message || "Failed to save snippet. Please try again.");
        throw err;
      }
    },
    [selectedChild, activeSnippetToSave, parentsDetails, activeWorkspaceEnv]
  );

  const handleTriggerSaveSprite = useCallback((spriteData) => {
    setActiveSpriteToSave(spriteData);
    setShowSaveSpriteDialog(true);
  }, []);

  const handleSaveSpriteToCloud = useCallback(
    async ({ name, description, spriteCode, thumbnail }) => {
      const childId = selectedChild?.id || selectedChild?.uid;
      if (!childId) {
        toast.error("Please select a student to save sprite to backpack.");
        return;
      }

      const payloadSpriteCode = spriteCode || activeSpriteToSave?.spriteCode;
      if (!payloadSpriteCode) {
        toast.error("Sprite data is empty. Please try saving the sprite again.");
        return;
      }

      try {
        await saveSprite({
          childId,
          parentUid: parentsDetails?.uid || selectedChild?.parentUid,
          name,
          description,
          spriteCode: payloadSpriteCode,
          thumbnail: thumbnail || activeSpriteToSave?.thumbnail,
        });

        toast.success(`Sprite "${name}" saved to Backpack!`);
      } catch (err) {
        console.error("Failed to save sprite:", err);
        toast.error(err.message || "Failed to save sprite. Please try again.");
        throw err;
      }
    },
    [selectedChild, activeSpriteToSave, parentsDetails]
  );

  const handleInsertSprite = useCallback(async (sprite) => {
    if (!sprite) return;

    try {
      let payloadSpriteCode = sprite.spriteCode || sprite.code || sprite.data;
      if (!payloadSpriteCode && sprite.filePath) {
        payloadSpriteCode = await loadSpriteData(sprite);
      }

      if (!payloadSpriteCode) {
        toast.error(`Could not retrieve sprite data for "${sprite.name || ""}".`);
        return;
      }

      const iframe =
        document.querySelector("iframe[title='Practido Visual Block Studio']") ||
        document.querySelector("iframe[title='Junior Blocks']");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: "ADD_SPRITE_FROM_BACKPACK",
            payload: {
              spriteCode: payloadSpriteCode,
              name: sprite.name,
            },
          },
          "*"
        );
      } else {
        toast.error("Could not find active workspace to add sprite.");
      }
    } catch (err) {
      console.error("Failed to load sprite from S3:", err);
      toast.error(err.message || "Failed to load sprite from cloud storage.");
    }
  }, []);

  const handleInsertSnippet = useCallback(async (snippet) => {
    if (!snippet) return;

    try {
      const isBlock =
        snippet.environment === "scratch" || snippet.environment === "junior";

      const loadedSnippet = await loadSnippetData(snippet);

      if (isBlock) {
        if (!loadedSnippet?.snippetJson) {
          toast.error(`Could not load blocks for snippet "${snippet.name || ""}".`);
          return;
        }
        const iframe =
          document.querySelector("iframe[title='Practido Visual Block Studio']") ||
          document.querySelector("iframe[title='Junior Blocks']");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: "INSERT_SCRATCH_SNIPPET",
              payload: {
                snippetJson: loadedSnippet.snippetJson,
                name: snippet.name,
              },
            },
            "*"
          );
        } else {
          toast.error("Could not find active workspace to insert blocks.");
        }
      } else {
        window.dispatchEvent(
          new CustomEvent("insert-backpack-code-snippet", {
            detail: loadedSnippet || snippet,
          })
        );
        toast.success(`Code from "${snippet.name}" inserted!`);
      }
    } catch (err) {
      console.error("Failed to load snippet from S3:", err);
      toast.error(err.message || "Failed to load snippet from cloud storage.");
    }
  }, []);

  return {
    showBackpackDrawer,
    setShowBackpackDrawer,
    showSaveSnippetDialog,
    setShowSaveSnippetDialog,
    activeSnippetToSave,
    showSaveSpriteDialog,
    setShowSaveSpriteDialog,
    activeSpriteToSave,
    activeContextEnv,
    handleTriggerSaveSnippet,
    handleSaveSnippetToCloud,
    handleTriggerSaveSprite,
    handleSaveSpriteToCloud,
    handleInsertSprite,
    handleInsertSnippet,
  };
};

export default useWorkspaceBackpackBridge;
